import React from 'react'
import { CONTACT_CONFIG } from '../../config/constants.js'
import { useLandingContent } from '../../hooks/i18n/usePageContent.js'
import * as TOKENS from '../chat/design-tokens'
import { STATS_COLS } from './hero-layout'

interface HeroSectionProps {
  onStartChat: () => void
  scrollToShowcase: () => void
  heroRef: React.RefObject<HTMLDivElement>
  onShapeChange?: (shape: string) => void
}

export const HeroSection: React.FC<HeroSectionProps> = ({
  onStartChat,
  scrollToShowcase,
  heroRef,
}) => {
  const content = useLandingContent()
  const { hero } = content

  return (
    <section
      ref={heroRef}
      className={`
        relative flex min-h-[calc(100dvh-5rem)] scroll-mt-24
        flex-col items-center justify-center overflow-hidden
        px-6 pb-14 pt-8
        sm:px-8 sm:pb-16
        md:px-12 md:pb-20
      `}
    >
      <div
        className={`
          relative z-10 flex w-full max-w-5xl flex-col gap-8
          ${TOKENS.ANIMATION.fadeInUp}
        `}
      >
        <div className="flex flex-col gap-6">
          <h1
            className={`
              ${TOKENS.TEXT_SIZE.hero}
              font-display font-semibold leading-none tracking-tighter
              ${TOKENS.TEXT.primary}
              text-left
            `}
          >
            {hero.titleLine1}
            {hero.titleLine2 ? (
              <>
                <br />
                <span className={TOKENS.TEXT.secondary}>{hero.titleLine2}</span>
              </>
            ) : null}
          </h1>

          <p
            className={`
              max-w-2xl text-xl font-light leading-relaxed
              ${TOKENS.TEXT.secondary} text-left
            `}
          >
            {hero.lead}
          </p>
        </div>

        <div className="flex justify-start pt-2">
          <div
            className={`
              inline-flex flex-wrap items-center gap-x-6 gap-y-3
              ${TOKENS.TEXT_SIZE.sm} font-mono uppercase tracking-wider
              ${TOKENS.TEXT.muted}
            `}
          >
            {hero.capabilities.map((label, i, arr) => (
              <React.Fragment key={label}>
                <span
                  className={`
                    cursor-default select-none
                    transition-colors duration-150
                    ${TOKENS.BRAND.accentHover}
                  `}
                >
                  {label}
                </span>
                {i < arr.length - 1 && (
                  <span className="opacity-20 text-xs select-none" aria-hidden="true">
                    /
                  </span>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="flex flex-col items-start gap-3 pt-2 sm:flex-row sm:items-center">
          <button
            type="button"
            onClick={() => onStartChat()}
            className={`
              group relative w-full overflow-hidden
              sm:w-auto
              ${TOKENS.PATTERNS.marketingPrimaryLarge}
            `}
          >
            <span className={TOKENS.INTERACTION.shimmer} />
            <span className="relative">{hero.ctas.primary}</span>
            <svg
              className="relative h-4 w-4 transition-transform duration-150 group-hover:translate-x-1"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              aria-hidden="true"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>

          <button
            type="button"
            onClick={() => window.open(CONTACT_CONFIG.SCHEDULING.BOOKING_URL, '_blank')}
            className={`
              group relative w-full overflow-hidden
              sm:w-auto
              ${TOKENS.PATTERNS.marketingSecondaryLarge}
            `}
          >
            <span className={TOKENS.INTERACTION.shimmer} />
            <span className="relative">{hero.ctas.bookConsultation}</span>
          </button>
        </div>

        <div
          className={`
            mt-6 grid grid-cols-1 divide-y border-t border-b
            ${TOKENS.BORDER.default}
            ${STATS_COLS[hero.stats.length] ?? 'md:grid-cols-2'} md:divide-y-0 md:divide-x
            divide-border
          `}
        >
          {hero.stats.map((stat, i) => (
            <div
              key={stat.label}
              className={`
                flex flex-col ${TOKENS.GAP.xs} py-6
                ${i === 0 ? 'md:pr-8' : i === 1 ? 'md:px-8' : 'md:pl-8'}
              `}
            >
              <span
                className={`
                  ${TOKENS.TEXT_SIZE.xs} font-mono uppercase tracking-widest
                  ${TOKENS.TEXT.muted}
                `}
              >
                {stat.label}
              </span>
              <span className={`text-sm font-medium tracking-tight ${TOKENS.TEXT.primary}`}>
                {stat.value}
              </span>
            </div>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={scrollToShowcase}
        className={`
          absolute bottom-8 left-1/2 flex -translate-x-1/2
          flex-col items-center gap-1
          opacity-40 transition-opacity duration-150 hover:opacity-80
          focus-visible:outline-none focus-visible:opacity-80
        `}
        aria-label={hero.scroll.ariaLabel}
      >
        <span
          className={`${TOKENS.TEXT_SIZE.tiny} font-mono uppercase tracking-[0.25em] ${TOKENS.TEXT.muted}`}
        >
          {hero.scroll.label}
        </span>
        <svg
          className={`h-4 w-4 ${TOKENS.ANIMATION.bounce} ${TOKENS.TEXT.muted}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          aria-hidden="true"
        >
          <path d="M7 13l5 5 5-5M7 6l5 5 5-5" />
        </svg>
      </button>
    </section>
  )
}
